import classnames from 'classnames';
import { format as dateFormat } from 'date-fns';
import { stringify } from 'qs';
import { ReactNode, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge } from '~/components/Badge/Badge';
import { BaseCard } from '~/components/Card';
import {
  ChallengeEventHistory,
  ChallengeEventRewardStatus,
  ChallengeEventWeekStatus,
} from '~/components/Challenge/Event/ChallengeEvent.hooks';
import { T } from '~/components/I18n';
import { QuestionV2 } from '~/components/Icon/QuestionV2';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { format, formatKaia } from '~/components/Util/number';
import styles from './ChallengeEventItem.module.scss';

export type ChallengeEventItemProps = {
  className?: string;
} & ChallengeEventHistory;

const badgeXltMap: Record<ChallengeEventWeekStatus, ReactNode> = {
  IN_PROGRESS: <T name="citizen_challenge_status_ongoing" />,
  END: <T name="citizen_challenge_status_ended" />,
  CLOSED: '',
};

const rewardStatusXltMap: Record<ChallengeEventRewardStatus, ReactNode> = {
  STANDBY: <T name="citizen_challenge_reward_status_ready" />,
  SUCCESS: <T name="citizen_challenge_reward_status_complete" />,
  UNREGISTERED_WALLET: <T name="citizen_challenge_reward_status_nowallet" />,
  EXPIRED: <T name="citizen_challenge_reward_status_expired" />,
  INSUFFICIENT_REWARD: <T name="citizen_challenge_reward_status_below" />,
  REJECTED: <T name="citizen_challenge_reward_status_refused" />,
  IMPOSSIBLE: <T name="citizen_challenge_reward_status_na" />,
};

const rewardLabelXltMap: Record<ChallengeEventRewardStatus, ReactNode> = {
  STANDBY: <T name="citizen_challenge_reward_estimation" />,
  SUCCESS: <T name="citizen_challenge_reward" />,
  REJECTED: <T name="citizen_challenge_reward" />,
  UNREGISTERED_WALLET: <T name="citizen_challenge_reward" />,
  EXPIRED: <T name="citizen_challenge_reward" />,
  INSUFFICIENT_REWARD: <T name="citizen_challenge_reward" />,
  IMPOSSIBLE: <T name="citizen_challenge_reward" />,
};

const rewardStatusTooltipXltMap: Partial<Record<ChallengeEventRewardStatus, ReactNode>> = {
  STANDBY: <T name="citizen_challenge_reward_status_ready_tooltip" />,
  REJECTED: <T name="citizen_challenge_reward_status_refused_tooltip" />,
  UNREGISTERED_WALLET: <T name="citizen_challenge_reward_status_nowallet_tooltip" />,
  EXPIRED: <T name="citizen_challenge_reward_status_expired_tooltip" />,
  INSUFFICIENT_REWARD: <T name="citizen_challenge_reward_status_below_tooltip" />,
  IMPOSSIBLE: <T name="citizen_challenge_reward_status_na_tooltip" />,
};

export const ChallengeEventItem = ({
  className,
  week,
  reward,
  rewardCurrency,
  weekStatus,
  rewardStatus,
  startAt,
  endAt,
  scoreUpScore,
  purchaseRewardScore,
}: ChallengeEventItemProps) => {
  const history = useHistory();

  const onPurchaseClick = useCallback(
    (e) => {
      e.preventDefault();
      history.push({
        pathname: '/challenge/purchase',
        search: stringify({ eventId: week }, { addQueryPrefix: true }),
      });
    },
    [history, week]
  );
  const onScoreClick = useCallback(
    (e) => {
      e.preventDefault();
      history.push({
        pathname: '/challenge/score',
        search: stringify({ eventId: week }, { addQueryPrefix: true }),
      });
    },
    [history, week]
  );

  const onTooltipClick = useCallback((e) => {
    e.preventDefault();
  }, []);

  return (
    <BaseCard
      className={classnames(styles.root, styles[weekStatus], styles[rewardStatus], className)}
      shadow={weekStatus === 'IN_PROGRESS'}
      size="large">
      <Badge className={styles.badge} color="black">
        {badgeXltMap[weekStatus]}
      </Badge>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <strong className={styles.round}>
            <T name="citizen_challenge_round" options={[week]} />
          </strong>
          <div className={styles.period}>
            {dateFormat(startAt, 'PP')} ~ {dateFormat(endAt, 'PP')}
          </div>
        </div>
        <p className={styles.rewardStatus}>
          {rewardStatusXltMap[rewardStatus]}
          {rewardStatusTooltipXltMap[rewardStatus] && (
            <Tooltip
              variant="secondary"
              direction="bottom"
              tooltipText={rewardStatusTooltipXltMap[rewardStatus]}
              onClick={onTooltipClick}>
              <QuestionV2 className={styles.icon} />
            </Tooltip>
          )}
        </p>
      </div>
      <hr className={styles.divider} />
      <div className={styles.body}>
        <DescriptionList className={styles.list} layout="horizontal">
          <dt>{rewardLabelXltMap[rewardStatus]}</dt>
          <dd>
            {formatKaia(reward)} {rewardCurrency}
          </dd>
          <p
            className={styles.link}
            onClick={onPurchaseClick}
            data-event="click"
            data-event-target="purchasescore_btn_in_challengehistory">
            <dt>
              <T name="citizen_challenge_purchasereward_score" />
            </dt>
            <dd>
              <T name="citizen_unit_score" options={[format(purchaseRewardScore)]} /> <Chevron />
            </dd>
          </p>
          <p
            className={styles.link}
            onClick={onScoreClick}
            data-event="click"
            data-event-target="scoreup_btn_in_challengehistory">
            <dt>
              <T name="citizen_challenge_scoreup_name" />
            </dt>
            <dd>
              <T name="citizen_unit_score" options={[format(scoreUpScore)]} /> <Chevron />
            </dd>
          </p>
        </DescriptionList>
      </div>
    </BaseCard>
  );
};
